import React, { useState } from "react";
import { ComponentProps, Case } from "../types/types";
import { supabaseService } from "../SupabaseService";

const DiverseVent: React.FC<ComponentProps> = ({
  cases,
  setCases,
  customers,
  setCustomers,
  openCaseForEditing,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [isAddingComment, setIsAddingComment] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const filteredCases = cases.filter(
    (case_) =>
      case_.kategori === "Diverse/Vent" &&
      (case_.kunde.toLowerCase().includes(searchTerm.toLowerCase()) ||
        case_.beskrivelse.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const editCase = (case_: Case) => {
    // Bare åpne for redigering, IKKE flytt til ny-registrering
    openCaseForEditing(case_);
  };

  const addComment = async (caseId: number) => {
    const newComment = prompt("Legg til kommentar:");
    if (!newComment) return;

    setIsAddingComment(true);
    setError(null);
    const originalCases = [...cases];

    try {
      // Finn casen som skal oppdateres
      const caseToUpdate = cases.find(c => c.id === caseId);
      if (!caseToUpdate) throw new Error("Case ikke funnet");

      // Oppdater lokalt først (optimistisk UI)
      const updatedComment = caseToUpdate.kommentar
        ? `${caseToUpdate.kommentar}\n${new Date().toLocaleDateString("nb-NO")}: ${newComment}`
        : `${new Date().toLocaleDateString("nb-NO")}: ${newComment}`;

      setCases((prev) =>
        prev.map((case_) =>
          case_.id === caseId
            ? { ...case_, kommentar: updatedComment }
            : case_
        )
      );

      // Lagre til Supabase
      const saved = await supabaseService.saveCase({
        ...caseToUpdate,
        kommentar: updatedComment
      });

      if (!saved) {
        throw new Error("Kunne ikke lagre kommentar");
      }
    } catch (error) {
      console.error("Comment error:", error);
      setCases(originalCases);
      setError("Kunne ikke legge til kommentar. Prøv igjen.");
      setTimeout(() => setError(null), 3000);
    } finally {
      setIsAddingComment(false);
    }
  };

  const moveToCategory = async (caseId: number, newCategory: string) => {
    setError(null);
    const originalCases = [...cases];

    try {
      const caseToUpdate = cases.find(c => c.id === caseId);
      if (!caseToUpdate) throw new Error("Case ikke funnet");

      // Oppdater lokalt først
      setCases((prev) =>
        prev.map((case_) =>
          case_.id === caseId
            ? { ...case_, kategori: newCategory, opprettet: new Date().toISOString() }
            : case_
        )
      );

      // Lagre til Supabase
      const saved = await supabaseService.saveCase({
        ...caseToUpdate,
        kategori: newCategory,
        opprettet: new Date().toISOString()
      });

      if (!saved) {
        throw new Error("Kunne ikke flytte sak");
      }
    } catch (error) {
      console.error("Move error:", error);
      setCases(originalCases);
      setError(`Kunne ikke flytte til ${newCategory}. Prøv igjen.`);
      setTimeout(() => setError(null), 3000);
    }
  };

  const getDaysInCategory = (case_: Case): number => {
    const caseDate = new Date(case_.opprettet);
    const now = new Date();
    return (
      Math.floor((now.getTime() - caseDate.getTime()) / (1000 * 60 * 60 * 24)) +
      1
    );
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border p-4">
      {/* Error message */}
      {error && (
        <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded flex items-center">
          <span className="mr-2">⚠️</span>
          {error}
        </div>
      )}

      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Diverse/Vent ({filteredCases.length})
        </h3>

        <div className="relative">
          <input
            type="text"
            placeholder="Søk i saker..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
          />
        </div>
      </div>

      <div className="mb-4 p-3 bg-gray-50 rounded-lg text-sm text-gray-600">
        <strong>💡 Info:</strong> Saker som venter på tilbud, kundeavgjørelse,
        eller andre avklaringer
      </div>

      <div className="space-y-3">
        {filteredCases.map((case_) => {
          const daysInCategory = getDaysInCategory(case_);

          return (
            <div
              key={case_.id}
              onClick={() => editCase(case_)}
              className={`border rounded-lg p-4 hover:shadow-md transition-all cursor-pointer ${
                daysInCategory > 7
                  ? "border-red-200 bg-red-50"
                  : daysInCategory > 3
                  ? "border-yellow-200 bg-yellow-50"
                  : "border-gray-200 hover:bg-gray-50"
              }`}
            >
              <div className="flex justify-between items-start mb-2">
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-1">
                    <h4 className="font-semibold text-gray-900">
                      {case_.kunde}
                    </h4>
                    <span
                      className={`text-xs px-2 py-1 rounded ${
                        daysInCategory > 7
                          ? "bg-red-100 text-red-800"
                          : daysInCategory > 3
                          ? "bg-yellow-100 text-yellow-800"
                          : "bg-gray-100 text-gray-800"
                      }`}
                    >
                      📅 {daysInCategory}{" "}
                      {daysInCategory === 1 ? "dag" : "dager"}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 mb-1">
                    {case_.maskinMerke} - {case_.beskrivelse}
                  </p>
                  <p className="text-xs text-gray-500">
                    Opprettet:{" "}
                    {new Date(case_.dato).toLocaleDateString("nb-NO")}
                  </p>

                  {case_.kommentar && (
                    <div className="mt-2 p-2 bg-blue-50 rounded text-xs">
                      <strong>📝 Kommentarer:</strong>
                      <div className="mt-1 whitespace-pre-line">
                        {case_.kommentar}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Handlingsknapper */}
              <div className="mt-3 flex justify-between items-center">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    addComment(case_.id);
                  }}
                  disabled={isAddingComment}
                  className={`px-3 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 ${
                    isAddingComment ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                >
                  {isAddingComment ? '💬 Legger til...' : '💬 Kommentar'}
                </button>
                
                <div className="flex space-x-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      moveToCategory(case_.id, "Service");
                    }}
                    className="px-3 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700"
                  >
                    → Service
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      moveToCategory(case_.id, "Arkiv");
                    }}
                    className="px-3 py-1 bg-gray-600 text-white text-xs rounded hover:bg-gray-700"
                  >
                    → Arkiv
                  </button>
                </div>
              </div>

              {/* Kontaktinfo for oppfølging */}
              <div className="mt-3 p-2 bg-gray-100 rounded text-xs">
                <strong>📞 Kontaktinfo:</strong>
                <div className="mt-1">
                  Tlf: {case_.tlf || "Ikke oppgitt"} | Email:{" "}
                  {case_.mail || "Ikke oppgitt"}
                </div>
              </div>

              {/* Potensielle varelinjer hvis noen */}
              {case_.varelinjer.length > 0 &&
                case_.varelinjer[0].varebeskrivelse && (
                  <div className="mt-3 p-2 bg-yellow-50 rounded text-xs">
                    <strong>💰 Foreslått tilbud:</strong>
                    {case_.varelinjer.map((linje, index) => (
                      <div key={index} className="mt-1">
                        • {linje.antall}x {linje.varebeskrivelse} - {linje.sum}{" "}
                        kr
                      </div>
                    ))}
                  </div>
                )}
            </div>
          );
        })}

        {filteredCases.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            <p>Ingen saker i vent/diverse</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default DiverseVent;